import type { Schedule } from '@/types';

/** TypeScript mirror of supabase/migrations/0002_due_functions.sql.
 *  Lets the board compute countdowns offline from the Dexie cache.
 *  All math runs in the device's local timezone (== the conservancy's). */

const DAY = 86_400_000;

function dateOnly(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((dateOnly(b).getTime() - dateOnly(a).getTime()) / DAY);
}

/** 'HH:MM' or 'HH:MM:SS' -> minutes since midnight */
function parseTime(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}

function atTime(day: Date, minutes: number): Date {
  const d = dateOnly(day);
  d.setMinutes(minutes);
  return d;
}

function localISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function dayAllowed(s: Schedule, day: Date): boolean {
  if (!s.days_of_week.includes(day.getDay())) return false;
  if (s.fasting_dates.includes(localISODate(day))) return false;
  if (s.feed_every_n_days != null) {
    const anchor = s.anchor_at ? new Date(s.anchor_at) : new Date(s.created_at);
    const diff = daysBetween(anchor, day);
    if (((diff % s.feed_every_n_days) + s.feed_every_n_days) % s.feed_every_n_days !== 0) return false;
  }
  return true;
}

function clampForward(s: Schedule, candidate: Date): Date | null {
  let c = new Date(candidate);
  const startMin = s.active_start ? parseTime(s.active_start) : null;
  const endMin = s.active_end ? parseTime(s.active_end) : null;
  for (let i = 0; i <= 100; i++) {
    const day = dateOnly(c);
    if (!dayAllowed(s, day)) {
      c = atTime(new Date(day.getTime() + DAY), startMin ?? 0);
      continue;
    }
    const minOfDay = c.getHours() * 60 + c.getMinutes();
    if (startMin != null && minOfDay < startMin) {
      c = atTime(day, startMin);
    }
    const minNow = c.getHours() * 60 + c.getMinutes();
    if (endMin != null && minNow > endMin) {
      c = atTime(new Date(day.getTime() + DAY), startMin ?? 0);
      continue;
    }
    return c;
  }
  return null;
}

export function computeNextDue(s: Schedule, now = new Date()): Date | null {
  if (!s.enabled) return null;
  let candidate: Date | null = null;

  if (s.stype === 'interval') {
    const base = s.last_fed_at
      ? new Date(s.last_fed_at)
      : s.anchor_at
        ? new Date(s.anchor_at)
        : now;
    candidate = clampForward(s, new Date(base.getTime() + (s.interval_minutes ?? 0) * 60_000));
  } else {
    const base = s.last_fed_at ? new Date(s.last_fed_at) : new Date(dateOnly(now).getTime() - 1);
    const sorted = [...(s.times ?? [])].map(parseTime).sort((a, b) => a - b);
    const baseDay = dateOnly(base);
    outer: for (let i = 0; i <= 100; i++) {
      const day = new Date(baseDay.getTime() + i * DAY);
      if (!dayAllowed(s, day)) continue;
      for (const t of sorted) {
        const slot = atTime(day, t);
        if (slot.getTime() > base.getTime()) {
          candidate = slot;
          break outer;
        }
      }
    }
  }

  if (!candidate) return null;
  if (s.snoozed_until) {
    const sn = new Date(s.snoozed_until);
    if (sn.getTime() > candidate.getTime()) return sn;
  }
  return candidate;
}

export type DueState = 'overdue' | 'due_soon' | 'scheduled' | 'idle';

export function dueState(nextDue: Date | null, now = new Date()): DueState {
  if (!nextDue) return 'idle';
  const diff = nextDue.getTime() - now.getTime();
  if (diff <= 0) return 'overdue';
  if (diff <= 60 * 60_000) return 'due_soon';
  return 'scheduled';
}

/** "2 hr 41 min late", "in 12 min", "due now" */
export function dueLabel(nextDue: Date, now = new Date()): string {
  const diffMin = Math.round((nextDue.getTime() - now.getTime()) / 60_000);
  if (Math.abs(diffMin) < 1) return 'due now';
  const abs = Math.abs(diffMin);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const span = h > 0 ? (m > 0 ? `${h} hr ${m} min` : `${h} hr`) : `${m} min`;
  return diffMin < 0 ? `${span} late` : `in ${span}`;
}

export function timeShort(d: Date): string {
  return d
    .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    .toLowerCase()
    .replace(' ', '');
}
